import { useEffect, useState } from 'react';
import { ArrowClockwise, Calendar, Package, TrendDown, Warning } from 'shared/icons';
import api from '../../services/api';
import { FormatDateTimeBr, FormatIsoDate } from '../../utils/date';
import { FormatInt, FormatMoney, Pluralize } from '../../utils/format';
import Button from '../ui/Button';
import ErrorCard from '../ui/ErrorCard';
import { Pill } from '../campaigns/campaignCardShared';
import { CampaignTypePill, FormatDaysUntil, IntelStatCard } from './intelligenceShared';

/**
 * BRIEFING — a primeira tela do dia da Inteligência de Mercado (Fase 4).
 *
 * Responde de uma vez "como está a operação AGORA": faturamento
 * (hoje × ontem × 7d), quantos anúncios pedem ação (perdendo/dividindo
 * catálogo, atenção, sem campanha) e o que muda de vigência nos próximos
 * dias. Cada card de contagem é CLICÁVEL e leva ao relatório/aba que explica
 * o número — o briefing aponta o problema, o relatório detalha.
 *
 * `onNavigate({tab, report?})` vem de pages/MarketIntelligence.jsx: a troca
 * de aba é estado da página, não deste painel (e é lá que 'attention' vira
 * navegação para a página Análise Meli).
 */
const Briefing = ({ onNavigate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Contador incrementado pelo botão Atualizar: o refetch de ENTRADA na aba
    // já acontece de graça porque o componente monta a cada visita (render
    // condicional por aba no MarketIntelligence.jsx).
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        // Guarda de unmount: trocar de aba no meio do fetch não pode fazer
        // setState em componente morto (mesmo padrão do badge de Atenção).
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const payload = await api.getMarketIntelligenceBriefing();
                if (!cancelled) {
                    setData(payload || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar o briefing.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [refreshTick]);

    if (error) {
        return (
            <ErrorCard
                title="Erro ao carregar o briefing"
                message={error}
                action="Tentar novamente"
                actionFn={() => setRefreshTick((tick) => tick + 1)}
            />
        );
    }

    if (loading && !data) {
        return (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950">
                        <div className="h-4 w-24 animate-pulse rounded bg-mono-200 dark:bg-mono-800" />
                        <div className="mt-3 h-8 w-32 animate-pulse rounded bg-mono-200 dark:bg-mono-800" />
                    </div>
                ))}
            </div>
        );
    }

    const revenue = data?.revenue || {};
    const endingSoon = Array.isArray(data?.campaigns_ending_soon) ? data.campaigns_ending_soon : [];
    const startingSoon = Array.isArray(data?.campaigns_starting_soon) ? data.campaigns_starting_soon : [];
    const unread = Number(data?.unread_notifications || 0);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-mono-500">
                    Gerado em {FormatDateTimeBr(data?.generated_at, { blank: '—' })}
                    {unread > 0 ? (
                        <span className="ml-3 inline-flex items-center gap-1 rounded-full bg-info-100 px-2 py-0.5 text-[11px] font-semibold text-info-800 dark:bg-info-900/40 dark:text-info-300">
                            {FormatInt(unread)} {Pluralize(unread, 'notificação não lida', 'notificações não lidas')}
                        </span>
                    ) : null}
                </p>
                <Button size="sm" variant="secondary" onClick={() => setRefreshTick((tick) => tick + 1)} disabled={loading}>
                    <ArrowClockwise size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Atualizar
                </Button>
            </div>

            {/* Faturamento: hoje é o número grande; ontem e 7d dão a régua. */}
            <div className="rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950">
                <p className="text-sm font-semibold text-black dark:text-white">Faturamento</p>
                <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-mono-500">Hoje</p>
                        <p className="mt-1 text-3xl font-bold tabular-nums text-black dark:text-white">
                            {FormatMoney(revenue.today)}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wide text-mono-500">Ontem</p>
                        <p className="mt-1 text-2xl font-semibold tabular-nums text-mono-700 dark:text-mono-300">
                            {FormatMoney(revenue.yesterday)}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-wide text-mono-500">Últimos 7 dias</p>
                        <p className="mt-1 text-2xl font-semibold tabular-nums text-mono-700 dark:text-mono-300">
                            {FormatMoney(revenue.last_7d)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Contagens acionáveis — cada card abre a leitura que gerou o número. */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <IntelStatCard
                    label="Perdendo catálogo"
                    value={FormatInt(data?.losing_catalog_count || 0)}
                    sub="Ver relatório de buybox"
                    tone="danger"
                    icon={TrendDown}
                    onClick={() => onNavigate?.({ tab: 'intel-reports', report: 'losing-catalog' })}
                />
                <IntelStatCard
                    label="Dividindo catálogo"
                    value={FormatInt(data?.sharing_catalog_count || 0)}
                    sub="Ver quem divide o 1º lugar"
                    tone="warning"
                    icon={TrendDown}
                    onClick={() => onNavigate?.({ tab: 'intel-reports', report: 'sharing-catalog' })}
                />
                <IntelStatCard
                    label="Precisam de atenção"
                    value={FormatInt(data?.attention_count || 0)}
                    sub="Abrir a aba Atenção"
                    tone="danger"
                    icon={Warning}
                    onClick={() => onNavigate?.({ tab: 'attention' })}
                />
                <IntelStatCard
                    label="Sem campanha"
                    value={FormatInt(data?.items_without_campaign_count || 0)}
                    sub="Ver anúncios sem campanha"
                    tone="mono"
                    icon={Package}
                    onClick={() => onNavigate?.({ tab: 'intel-reports', report: 'without-campaign' })}
                />
            </div>

            {/* Vigências próximas (≤5 dias) — o dado urgente do briefing. */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <SoonList
                    title="Campanhas vencendo"
                    icon={Warning}
                    rows={endingSoon}
                    dateKey="finish_date"
                    daysKey="days_left"
                    daysTone="danger"
                    emptyMessage="Nenhuma campanha vence nos próximos 5 dias."
                />
                <SoonList
                    title="Campanhas começando"
                    icon={Calendar}
                    rows={startingSoon}
                    dateKey="start_date"
                    daysKey="days_until"
                    daysTone="info"
                    emptyMessage="Nenhuma campanha começa nos próximos 5 dias."
                />
            </div>

            <p className="text-xs text-mono-500">
                Quer a agenda completa de vigências? Abra o{' '}
                <button
                    type="button"
                    className="font-semibold text-black underline dark:text-white"
                    onClick={() => onNavigate?.({ tab: 'intel-calendar' })}
                >
                    Calendário
                </button>
                .
            </p>
        </div>
    );
};

/**
 * Lista compacta de campanhas com vigência próxima. O DESTAQUE é a contagem
 * regressiva (days_left/days_until) — a data absoluta fica de apoio.
 */
const SoonList = ({ title, icon: Icon, rows, dateKey, daysKey, daysTone, emptyMessage }) => (
    <div className="rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
        <div className="flex items-center gap-2 border-b border-mono-100 px-4 py-2.5 dark:border-mono-900">
            <Icon size={16} className="text-mono-500" />
            <p className="text-sm font-semibold text-black dark:text-white">{title}</p>
            <span className="ml-auto rounded-full bg-mono-100 px-2 py-0.5 text-xs font-semibold text-mono-600 dark:bg-mono-800 dark:text-mono-300">
                {rows.length}
            </span>
        </div>
        {rows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-mono-500">{emptyMessage}</p>
        ) : (
            <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                {rows.map((row) => (
                    <li key={row.campaign_id} className="flex items-center gap-3 px-4 py-2.5">
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-black dark:text-white" title={row.name || ''}>
                                {row.name || row.campaign_id}
                            </p>
                            <p className="mt-0.5 flex items-center gap-2 text-xs text-mono-500">
                                <CampaignTypePill type={row.type} />
                                <span>{FormatIsoDate(row[dateKey], { blank: '—' })}</span>
                            </p>
                        </div>
                        <Pill tone={Number(row[daysKey]) <= 1 ? 'danger' : daysTone} className="shrink-0">
                            {FormatDaysUntil(row[daysKey])}
                        </Pill>
                    </li>
                ))}
            </ul>
        )}
    </div>
);

export default Briefing;
